import { t } from "i18next";
import { isEqual } from "lodash-es";

import i18n from "@/app/i18n";
import {
  FALLBACK_LANGUAGE,
  MeasurementSystems,
  SUPPORTED_LANGUAGES,
} from "../app/constants";
import {
  type Action,
  ActionType,
  type SupportedLanguage,
} from "../config/types";
import { SPECIAL_TAGS } from "../native_routes/constants";
import type { RouteIndication } from "../navigation/types";
import { IndicationType, OrientationTypesSdk } from "./navigation";
import { AllowedHtmlTags } from "./types";

export const getPoiNameByLang = (poi: any, lang: string) => {
  if (poi.categoryId == -1) return t("navigation.current_location");
  if (lang === "en") return poi.name;
  if (SUPPORTED_LANGUAGES.includes(lang)) {
    if (poi.customFields?.length !== 0) {
      const customFieldName = poi.customFields.find(
        (el: any) =>
          el?.key === `name_${lang}` || (lang === "ar" && el?.key === "arabic"),
        //TODO: Remove dirty trick for SAGP
      );
      if (customFieldName) return customFieldName?.value;
    }
    return poi.name;
  }
  return "";
};

export const checkMobileBrowser = (
  navigator: Navigator,
  _window: Window,
): boolean => {
  const deviceVendor = navigator.userAgent || navigator.vendor; //|| window.opera;

  if (
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      deviceVendor,
    ) ||
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
      deviceVendor.substr(0, 4),
    )
  ) {
    return true;
  }
  return false;
};

export const isIosSafari = (): boolean => {
  const ua = navigator.userAgent || "";

  const isIphone = /iPhone/i.test(ua);
  const isWebKit = /WebKit/i.test(ua);
  const isChrome = /CriOS/i.test(ua); // Chrome on iOS
  const isFirefox = /FxiOS/i.test(ua); // Firefox on iOS
  const isEdge = /EdgiOS/i.test(ua); // Edge on iOS

  const isSafariOrWebView = isWebKit && !isChrome && !isFirefox && !isEdge;

  return isIphone && isSafariOrWebView;
};

export const getPoiInfoByLang = (poi: any, lang: string) => {
  if (lang === "en") return poi.info;
  if (SUPPORTED_LANGUAGES.includes(lang)) {
    if (poi.customFields?.length !== 0) {
      const customFieldInfo = poi.customFields.find(
        (el: any) =>
          el?.key === `info_${lang}` ||
          (lang === "ar" && el?.key === "ar_desc"),
        //TODO: Remove dirty trick for SAGP
      );
      if (customFieldInfo) return customFieldInfo?.value;
    }
    return poi.info;
  }
  return "";
};

export const createInterval = (fn: () => void, delay: number) => {
  let timeoutId: NodeJS.Timeout | undefined;
  const start = () => {
    fn();
    timeoutId = setTimeout(start, delay);
  };
  const stop = () => {
    timeoutId && clearTimeout(timeoutId);
  };

  return {
    start,
    stop,
  };
};

export const ping = (
  url: string,
  onSuccess: () => void,
  onError: () => void,
) => {
  fetch(url, {
    cache: "no-store",
  })
    .then((r) => {
      if (r.ok && r.status >= 200 && r.status < 300) {
        onSuccess();
      } else {
        onError();
      }
    })
    .catch(() => {
      onError();
    });
};

export const roundToNearestStep = (number: number, step: number) =>
  Math.round(number / step) * step;

export function difference(obj1: JSONValue, obj2: JSONValue): JSONValue {
  const result: JSONValue = {};

  for (const key in obj2) {
    if (
      typeof obj2[key] === "object" &&
      obj2[key] !== null &&
      obj1[key] !== null
    ) {
      if (Array.isArray(obj2[key])) {
        if (
          !arraysHaveSameValues(
            Array.from((obj1[key] as unknown as []) ?? []),
            Array.from(obj2[key] as unknown as []),
          )
        ) {
          result[key] = obj2[key];
        }
      } else {
        const nestedDifference = difference(
          (obj1[key] as JSONValue) || {},
          obj2[key] as JSONValue,
        );
        if (Object.keys(nestedDifference).length > 0) {
          result[key] = nestedDifference;
        }
      }
    } else if (
      obj1[key] === undefined ||
      JSON.stringify(obj1[key]) !== JSON.stringify(obj2[key])
    ) {
      result[key] = obj2[key];
    }
  }

  return result;
}
interface JSONValue {
  [x: string]: JSONValue | string | boolean | number | null;
}

export function cleanJSON(obj: JSONValue | null): JSONValue {
  // verificamos si el objeto es un objeto literal
  if (obj == null || obj !== Object(obj)) return {};

  // filtramos cada propiedad del objeto
  const cleanedObj = Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      // comprobamos si el valor de la propiedad es un objeto y si es así, llamamos a la función de limpieza de nuevo
      if (typeof v === "object" && v !== null) {
        if (Array.isArray(v)) {
          return [k, v];
        }
        return [k, cleanJSON(v)];
      }
      // si el valor de la propiedad no está vacío, lo incluimos en el nuevo objeto
      return [k, v];
    }),
  );

  // Filtramos las propiedades que sean objetos vacíos
  const result = Object.fromEntries(
    Object.entries(cleanedObj).filter(([_, v]) => {
      return !(
        typeof v === "object" &&
        v !== null &&
        Object.keys(v).length === 0
      );
    }),
  );

  return result;
}

export const isEmptyObject = (obj: any) =>
  obj ? Object.keys(obj).length < 1 : true;

export const joinUrlPath = (basePath: string, newPath: string): string => {
  const base = basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
  const path = newPath.startsWith("/") ? newPath.slice(1) : newPath;
  return `${base}/${path}`;
};

export const uuidv4 = (): string => {
  // Public Domain/MIT
  let d = new Date().getTime(); //Timestamp
  let d2 =
    (typeof performance !== "undefined" &&
      performance.now &&
      performance.now() * 1000) ||
    0; //Time in microseconds since page-load or 0 if unsupported
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    let r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = ((d + r) % 16) | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = ((d2 + r) % 16) | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const generateRandomNumber = (numChars: number): number => {
  if (numChars <= 0) {
    throw new Error("Number of characters must be greater than 0.");
  }

  const min = 10 ** (numChars - 1);
  const max = 10 ** numChars - 1;

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Use local storage by first checking if it is available on the current browser,
 * as cookies might be blocked.
 * @param key
 * @param value
 * @returns
 */
export const safeLocalStorage = {
  getItem: (key: string) => {
    try {
      if (localStorage) {
        return localStorage.getItem(key);
      } else {
        console.warn("localStorage is not available.");
        return null;
      }
    } catch (error) {
      console.warn("Error accessing localStorage:", error);
      return null;
    }
  },
  removeItem: (key: string) => {
    try {
      if (localStorage) {
        localStorage.removeItem(key);
      } else {
        console.warn("localStorage is not available.");
      }
    } catch (error) {
      console.warn("Error accessing localStorage:", error);
    }
  },
  setItem: (key: string, value: any) => {
    try {
      if (localStorage) {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        console.warn("localStorage is not available.");
      }
    } catch (error) {
      console.warn("Error accessing localStorage:", error);
    }
  },
};

/**
 * Given a string with HTML tags, extract only the text
 * @param htmlString
 * @returns
 */
export const extractTextFromHTML = (htmlString: string) => {
  if (!htmlString || htmlString.length == 0) return "";
  // If iframe, extract content on srcdoc
  if (htmlString.includes("<iframe")) {
    const srcdocRegex = /<iframe.*srcdoc="([^"]*)".*/i;
    const match = srcdocRegex.exec(htmlString);
    htmlString = match ? match[1] : "";
  }

  // Remove style tag if exists
  if (htmlString.includes("<style")) {
    htmlString = htmlString.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/g, "");
  }

  // Extract text from html
  return htmlString.replace(/<[^>]+>/g, "");
};

export function measureTime(myFunction: any, args: any, numberRuns: number) {
  const start = performance.now();
  for (let i = 0; i < numberRuns; i++) {
    myFunction(...args);
  }
  const end = performance.now();
  console.log(
    `Execution time for ${numberRuns} runs: ${end - start} milliseconds`,
  );
}

function arraysHaveSameValues(array1: any[], array2: any[]) {
  // Check if the arrays have the same length
  if (array1.length !== array2.length) {
    return false;
  }

  // Sort the arrays just in case
  array1.sort();
  array2.sort();

  // Compare each element in the arrays
  for (let i = 0; i < array1.length; i++) {
    if (array1[i] !== array2[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Property Change Handlers
 *
 * This section contains functions used for handling and detecting changes in component properties.
 * - `poiPropsChanged`: Determines if any POI-related properties have changed.
 * - `visualPropsChanged`: Checks for changes in visual-related properties.
 * - `_hasAnyPropChangedDeep`: Utility function to deeply check for changes in a set of properties.
 * - `_hasAnyPropChanged`: Utility function to shallowly check for changes in a set of properties.
 *
 * These functions are essential for optimizing component updates and ensuring that state changes
 * are handled efficiently when certain properties of the component are modified.
 */
export function hasAnyPropChangedDeep<T>(
  props: T,
  oldProps: T,
  ...propNames: (keyof T)[]
): boolean {
  for (const propName of propNames) {
    if (!isEqual(props[propName], oldProps[propName])) {
      return true;
    }
  }
  return false;
}

export function hasAnyPropChanged<T>(
  props: T,
  oldProps: T,
  ...propNames: (keyof T)[]
): boolean {
  for (const propName of propNames) {
    if (props[propName] !== oldProps[propName]) {
      return true;
    }
  }
  return false;
}
/*
 * Maps a numeric value from one range to another.
 *
 * @param {number} value - The input value to be mapped.
 * @param {number} oldMin - The minimum value of the current range.
 * @param {number} oldMax - The maximum value of the current range.
 * @param {number} newMin - The minimum value of the target range.
 * @param {number} newMax - The maximum value of the target range.
 * @returns {number} - The mapped value in the target range.
 *
 * @example
 * // Map the value 50 from the range [0, 100] to the range [0, 1]
 * const result = mapNumber(50, 0, 100, 0, 1);
 * // Result: 0.5
 */
export const mapNumber = (
  value: number,
  oldMin: number,
  oldMax: number,
  newMin: number,
  newMax: number,
) => {
  if (value < oldMin) return newMin;
  if (value > oldMax) return newMax;

  const percentage = (value - oldMin) / (oldMax - oldMin);
  return percentage * (newMax - newMin) + newMin;
};

/**
 * Check if a given dictionary is empty or null
 * @param obj dictionary or null
 * @returns Boolean
 */
export const isEmptyDict = (obj: object | null | undefined) => {
  return Object.keys(obj || {}).length === 0;
};

/**
 * Calculates estimated time walking
 * @param distance path length in meters
 * @returns time un minutes
 */
export const calculateTimeWalking = (distance: number) => {
  return Math.ceil((distance * 60) / 5000);
};

export const getRootFontSize = (): number => {
  const computedStyle = window.getComputedStyle(
    document.getElementById("root"),
  );
  const fontSize = parseFloat(computedStyle.getPropertyValue("font-size"));

  return fontSize;
};

export const calculateBaseFontGainance = (): number => {
  return getRootFontSize() / 16;
};

export const getActionModalTitle = ({
  tagActions,
  tags,
  lang,
}: {
  tagActions: Action[];
  tags: string[];
  lang: SupportedLanguage;
}): string => {
  const titles = tagActions
    .filter((tAct) => tags.includes(tAct.triggerOptions.tag))
    .map((tAct) => {
      if (tAct?.action == ActionType.MODAL && !!tAct?.actionOptions.modal.title)
        return tAct?.actionOptions.modal.title;
    });

  if (!titles.length) return "";

  const localizedTitle = titles.at(0);

  return (
    localizedTitle[lang] ||
    localizedTitle[FALLBACK_LANGUAGE] ||
    tags.at(0) ||
    ""
  );
};

export const translateIndicationMessage = ({
  indication,
  relatedPoiName,
  nextIndication,
  floorName,
  metadata: {
    actions,
    lang,
    source = "directions",
    currentMeasurementSystem = MeasurementSystems.metric,
  },
}: {
  indication: RouteIndication;
  relatedPoiName?: string;
  nextIndication?: RouteIndication | null;
  floorName?: string;
  metadata?: {
    actions?: Action[];
    lang?: SupportedLanguage;
    source?: "navigation" | "directions";
    currentMeasurementSystem?: MeasurementSystems;
  };
}): string | null => {
  switch (indication.indicationType) {
    case IndicationType.CHANGE_FLOOR: {
      const goingUp = indication.distanceToNextLevel > 0;
      const goingDown = indication.distanceToNextLevel < 0;
      if (relatedPoiName) {
        return goingUp
          ? i18n.t(`navigation.steps.go_up_using`, {
              floorName,
              poiName: relatedPoiName,
            })
          : goingDown
            ? i18n.t(`navigation.steps.go_down_using`, {
                floorName,
                poiName: relatedPoiName,
              })
            : i18n.t(`navigation.steps.change_floor_using`, {
                floorName,
                poiName: relatedPoiName,
              });
      } else {
        return goingUp
          ? i18n.t(`navigation.steps.go_up`, { floorName })
          : goingDown
            ? i18n.t(`navigation.steps.go_down`, { floorName })
            : i18n.t(`navigation.steps.change_floor`, { floorName });
      }
    }

    case IndicationType.TURN: {
      const orientationKey =
        indication.orientationType === OrientationTypesSdk.LEFT
          ? "left"
          : indication.orientationType === OrientationTypesSdk.RIGHT
            ? "right"
            : "around";

      const indicationContent = relatedPoiName
        ? i18n.t(`navigation.steps.turn_${orientationKey}_to`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
            poiName: relatedPoiName,
          })
        : (i18n.t(`navigation.steps.turn_${orientationKey}`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
          }) as string);

      if (source == "directions" || orientationKey == "around") {
        return indicationContent;
      } else {
        return lowerCaseFirstIndicationLetter(
          i18n.t(`navigation.steps.turn_connector`),
          indicationContent,
        );
      }
    }

    case IndicationType.TURN_SLIGHT: {
      const orientationKey =
        indication.orientationType === OrientationTypesSdk.LEFT
          ? "left"
          : "right";

      const indicationContent = relatedPoiName
        ? i18n.t(`navigation.steps.slight_turn_${orientationKey}_to`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
            poiName: relatedPoiName,
          })
        : i18n.t(`navigation.steps.slight_turn_${orientationKey}`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
          });

      if (source == "directions") {
        return indicationContent;
      } else {
        return lowerCaseFirstIndicationLetter(
          i18n.t(`navigation.steps.turn_connector`),
          indicationContent,
        );
      }
    }

    case IndicationType.LOOK_AT: {
      const orientationKey =
        indication.orientationType === OrientationTypesSdk.LEFT
          ? "left"
          : "right";

      return relatedPoiName
        ? i18n.t(`navigation.steps.look_at_${orientationKey}_to`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
            poiName: relatedPoiName,
          })
        : i18n.t(`navigation.steps.look_at_${orientationKey}`, {
            distance: Math.floor(indication.distance),
            formatParams: {
              distance: {
                measurement_system: currentMeasurementSystem,
              },
            },
          });
    }

    case IndicationType.WALK:
    case IndicationType.GO_AHEAD: {
      const distanceParams = {
        distance: Math.floor(indication.distance),
        floorName,
        ...(relatedPoiName && { poiName: relatedPoiName }),
      };

      if (nextIndication?.indicationType === IndicationType.TURN) {
        if (nextIndication?.orientationType === OrientationTypesSdk.LEFT) {
          return i18n.t(
            relatedPoiName
              ? "navigation.steps.walk_distance_to_and_turn_left"
              : "navigation.steps.walk_distance_and_turn_left",

            {
              ...distanceParams,
              formatParams: {
                distance: {
                  measurement_system: currentMeasurementSystem,
                },
              },
            },
          );
        }

        if (nextIndication?.orientationType === OrientationTypesSdk.RIGHT) {
          return i18n.t(
            relatedPoiName
              ? "navigation.steps.walk_distance_to_and_turn_right"
              : "navigation.steps.walk_distance_and_turn_right",
            {
              ...distanceParams,
              formatParams: {
                distance: {
                  measurement_system: currentMeasurementSystem,
                },
              },
            },
          );
        }
      }

      if (nextIndication?.indicationType === IndicationType.TURN_SLIGHT) {
        if (nextIndication?.orientationType === OrientationTypesSdk.LEFT) {
          return i18n.t(
            relatedPoiName
              ? "navigation.steps.walk_distance_to_and_turn_slight_left"
              : "navigation.steps.walk_distance_and_turn_slight_left",
            {
              ...distanceParams,
              formatParams: {
                distance: {
                  measurement_system: currentMeasurementSystem,
                },
              },
            },
          );
        }

        if (nextIndication?.orientationType === OrientationTypesSdk.RIGHT) {
          return i18n.t(
            relatedPoiName
              ? "navigation.steps.walk_distance_to_and_turn_slight_right"
              : "navigation.steps.walk_distance_and_turn_slight_right",
            {
              ...distanceParams,
              formatParams: {
                distance: {
                  measurement_system: currentMeasurementSystem,
                },
              },
            },
          );
        }
      }

      return i18n.t(
        relatedPoiName
          ? "navigation.steps.walk_distance_to"
          : "navigation.steps.walk_distance",
        {
          ...distanceParams,
          formatParams: {
            distance: {
              measurement_system: currentMeasurementSystem,
            },
          },
        },
      );
    }
    case IndicationType.PASS_THROUGH: {
      const matchedTag = indication.metaData?.find((tag) =>
        SPECIAL_TAGS.includes(tag),
      );

      if (matchedTag) {
        const tagWithoutHash = matchedTag.startsWith("#")
          ? matchedTag.substring(1)
          : matchedTag;

        return i18n.t("navigation.steps.pass_through_" + tagWithoutHash);
      }

      const firstTag = indication.metaData?.at(0) ?? "unknown";
      const tagWithoutHash = firstTag.startsWith("#")
        ? firstTag.substring(1)
        : firstTag;

      return i18n.t("navigation.steps.pass_through_custom", {
        name: tagWithoutHash,
      });
    }

    case IndicationType.END:
    case IndicationType.DESTINATION_REACHED:
      return i18n.t(`navigation.destination_reached`);
    default:
      return null;
  }
};

export const containsKnownTag = (str: string): boolean =>
  AllowedHtmlTags.some((tag) => str.includes(`<${tag}`));

export const decodeHtmlEntities = (text: string): string => {
  const parser = new DOMParser();
  const decodedString = parser.parseFromString(text, "text/html").body
    .textContent;
  return decodedString;
};

// Compute the module of a number
export const mod = (n: number, m: number) => {
  return ((n % m) + m) % m;
};

const lowerCaseFirstIndicationLetter = (
  conector: string,
  indication: string,
): string => {
  return `${conector} ${lowerFirstLetter(indication)}`;
};

export const lowerFirstLetter = (phrase: string): string => {
  return `${String(phrase).charAt(0).toLowerCase() + String(phrase).slice(1)}`;
};

export const capitalizeFirstLetter = (str: string): string =>
  str.charAt(0).toLocaleUpperCase() + str.slice(1);

export const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function detectPlatform() {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh|Mac OS X/.test(ua) && !isIOS;
  const isWindows = /Windows/.test(ua);
  const isLinux = /Linux/.test(ua) && !isAndroid;
  return { isAndroid, isIOS, isLinux, isMac, isWindows };
}

export const snakeToCamelCase = (s: string) =>
  s.replace(/_([a-z0-9])/g, (_m, g) => g.toUpperCase());
